// web/src/components/browser/ContextMenu.tsx
// Right-click menu. Renders at absolute coordinates, dismisses on
// outside-click or Escape. The parent tracks position + target via
// a small onContextMenu handler.

import { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";

export interface ContextMenuState {
  x: number;
  y: number;
  targets: string[];
}

interface Props {
  state: ContextMenuState | null;
  onClose: () => void;
  onExport: () => void;
  onPreview: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  onCopyChunkId: () => void;
}

export function ContextMenu({
  state,
  onClose,
  onExport,
  onPreview,
  onRename,
  onMove,
  onDelete,
  onCopyChunkId,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!state) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [state, onClose]);

  if (!state) return null;
  const single = state.targets.length === 1;

  return (
    <div
      ref={ref}
      style={{ left: state.x, top: state.y }}
      className="fixed z-50 w-56 rounded-md border border-border bg-bg-raised p-1 text-sm shadow-2xl"
    >
      <Item onClick={onExport} kbd="⌘E">↓ Export to disk</Item>
      <Item onClick={onPreview} kbd="␣" disabled={!single}>
        👁 Preview (memory-only)
      </Item>
      <Separator />
      <Item onClick={onRename} kbd="F2" disabled={!single}>
        ✎ Rename
      </Item>
      <Item onClick={onMove}>→ Move to…</Item>
      <Separator />
      <Item onClick={onCopyChunkId} disabled={!single}>
        Copy chunk id
      </Item>
      <Separator />
      <Item onClick={onDelete} kbd="⌫" danger>
        🗑 Delete
      </Item>
    </div>
  );
}

function Item({
  children,
  onClick,
  kbd,
  danger,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  kbd?: string;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-text-soft hover:bg-accent-bg hover:text-text disabled:opacity-30 disabled:hover:bg-transparent",
        danger && "text-danger hover:bg-danger/10 hover:text-danger",
      )}
    >
      <span className="flex-1">{children}</span>
      {kbd && <span className="font-mono text-[10px] text-text-dim">{kbd}</span>}
    </button>
  );
}

function Separator() {
  return <div className="my-1 h-px bg-border" />;
}
